import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, FileText, CheckCircle2, PenTool, Lock, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import type { Promoter } from '../types/promoter';

export default function PromoterSignPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [promoter, setPromoter] = useState<Promoter | null>(null);
  const [loading, setLoading] = useState(true);
  const [typedName, setTypedName] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    async function load() {
      if (!user) { setLoading(false); return; }
      try {
        const snap = await getDoc(doc(db, 'promoters', user.uid));
        if (snap.exists()) setPromoter({ ...(snap.data() as Promoter), uid: user.uid });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user]);

  const handleSign = async () => {
    if (!user) return;
    if (!consent) { toast.error('Veuillez cocher la case de consentement'); return; }
    if (!typedName.trim()) { toast.error('Recopiez votre nom et prénom'); return; }
    setSubmitting(true);
    try {
      const token = await user.getIdToken();
      const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/promoters/sign-contract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ typedName, consent: true }),
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.error || 'Erreur');
      setDone(true);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return <div className="min-h-screen bg-[#0D0D0F] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF5C00]/20 border-t-[#FF5C00] rounded-full animate-spin" />
    </div>;
  }

  // Non connecté
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0D0D0F] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <Lock className="w-8 h-8 text-[#FF5C00] mx-auto mb-4" />
          <h1 className="text-2xl font-black mb-3">Connexion requise</h1>
          <p className="text-gray-400 text-sm mb-6">Connectez-vous avec le compte de votre candidature promoteur.</p>
          <button onClick={() => navigate('/')} className="bg-[#FF5C00] hover:bg-[#e05000] text-white px-8 py-3.5 rounded-2xl font-black text-sm transition-all">Se connecter</button>
        </div>
      </div>
    );
  }

  // Pas de profil promoteur / pas encore validé
  if (!promoter || promoter.status !== 'active') {
    return (
      <div className="min-h-screen bg-[#0D0D0F] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <ShieldCheck className="w-8 h-8 text-gray-500 mx-auto mb-4" />
          <h1 className="text-2xl font-black mb-3">Contrat indisponible</h1>
          <p className="text-gray-400 text-sm mb-6">
            {!promoter
              ? "Aucune candidature promoteur n'est associée à ce compte."
              : promoter.status === 'rejected'
              ? "Votre candidature n'a pas été retenue."
              : "Votre profil est en cours de validation. Le contrat sera disponible une fois validé."}
          </p>
          <button onClick={() => navigate('/home')} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-3.5 rounded-2xl font-black text-sm transition-all">Retour</button>
        </div>
      </div>
    );
  }

  // Déjà signé
  if (done || promoter.codeActive) {
    return (
      <div className="min-h-screen bg-[#0D0D0F] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-black mb-3">Contrat signé 🎉</h1>
          <p className="text-gray-400 text-sm leading-relaxed mb-6">
            Votre code affilié est activé. Vous pouvez maintenant soumettre des festivals et suivre vos commissions.
          </p>
          {promoter.affiliateCode && (
            <div className="bg-[#161618] border border-[#FF5C00]/20 rounded-2xl px-6 py-4 mb-6">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Votre code affilié</p>
              <p className="text-2xl font-black text-[#FF5C00]">{promoter.affiliateCode}</p>
            </div>
          )}
          <button onClick={() => navigate('/promoter/dashboard')} className="bg-[#FF5C00] hover:bg-[#e05000] text-white px-8 py-3.5 rounded-2xl font-black text-sm transition-all">
            Accéder à mon dashboard
          </button>
        </div>
      </div>
    );
  }

  const expectedName = `${promoter.firstName} ${promoter.lastName}`;

  return (
    <div className="min-h-screen bg-[#0D0D0F] text-white">
      <div className="max-w-xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 bg-[#FF5C00]/10 border border-[#FF5C00]/20 text-[#FF5C00] text-xs font-black px-3 py-1.5 rounded-full mb-5">
            <PenTool className="w-3.5 h-3.5" /> SIGNATURE DU CONTRAT
          </div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight mb-2">Signez votre contrat d'apporteur d'affaires</h1>
          <p className="text-gray-400 text-sm leading-relaxed">
            Dernière étape avant l'activation de votre code affilié <strong className="text-[#FF5C00]">{promoter.affiliateCode}</strong>.
          </p>
        </div>

        {/* Lien vers le PDF */}
        {promoter.contractUrl && (
          <a href={promoter.contractUrl} target="_blank" rel="noreferrer"
            className="flex items-center gap-3 bg-[#161618] border border-white/8 rounded-2xl p-4 mb-6 hover:border-white/20 transition-all group">
            <div className="w-10 h-10 rounded-xl bg-[#FF5C00]/10 flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-[#FF5C00]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-white">Lire le contrat (PDF)</p>
              <p className="text-xs text-gray-500">Contrat d'apporteur d'affaires — Festin</p>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" />
          </a>
        )}

        {/* Consentement */}
        <button
          type="button"
          onClick={() => setConsent(v => !v)}
          className="flex items-start gap-3 text-left w-full mb-5 group"
        >
          <div className={`mt-0.5 w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center transition-all ${
            consent ? 'bg-[#FF5C00] border-[#FF5C00]' : 'border-white/20 group-hover:border-white/40'
          }`}>
            {consent && <CheckCircle2 className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
          </div>
          <span className="text-sm text-gray-300 leading-relaxed">
            Je reconnais avoir lu et j'accepte les termes du contrat d'apporteur d'affaires Festin. Je comprends que cette signature électronique a valeur d'engagement (art. 1366-1367 du Code civil).
          </span>
        </button>

        {/* Recopie du nom */}
        <div className="mb-6">
          <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-2">
            Recopiez votre nom et prénom pour signer
          </label>
          <input
            type="text"
            value={typedName}
            onChange={e => setTypedName(e.target.value)}
            placeholder={expectedName}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF5C00]/50"
          />
          <p className="text-[11px] text-gray-600 mt-1.5">Recopiez exactement : <span className="text-gray-400 font-medium">{expectedName}</span></p>
        </div>

        <button
          onClick={handleSign}
          disabled={submitting || !consent || !typedName.trim()}
          className="w-full flex items-center justify-center gap-3 bg-[#FF5C00] hover:bg-[#e05000] disabled:opacity-40 disabled:cursor-not-allowed text-white py-4 rounded-2xl font-black text-sm transition-all"
        >
          <ShieldCheck className="w-4 h-4" />
          {submitting ? 'Signature en cours…' : 'Signer et activer mon code'}
        </button>
        <p className="text-center text-[11px] text-gray-600 mt-3">
          Signature électronique simple, horodatée. Votre code sera activé immédiatement.
        </p>
      </div>
    </div>
  );
}
